export interface ScreenPoint { x: number; y: number }
export interface GridPoint extends ScreenPoint { elevation: number }
export interface CameraOptions { tileWidth?: number; tileHeight?: number; zoom?: number; panX?: number; panY?: number; minZoom?: number; maxZoom?: number }

const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(maximum, value));
const ease = (t: number): number => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export class Camera {
  readonly tileWidth: number;
  readonly tileHeight: number;
  readonly minZoom: number;
  readonly maxZoom: number;
  zoom: number;
  panX: number;
  panY: number;
  private targetZoom: number;
  private targetX: number;
  private targetY: number;
  private velocityX = 0;
  private velocityY = 0;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private lastTime = 0;
  private focus?: { startX: number; startY: number; startZoom: number; endX: number; endY: number; endZoom: number; start: number; duration: number } | undefined;
  private readonly pointers = new Map<number, ScreenPoint>();
  private pinchDistance = 0;

  constructor(options: CameraOptions = {}) {
    this.tileWidth = options.tileWidth ?? 160;
    this.tileHeight = options.tileHeight ?? 80;
    this.minZoom = options.minZoom ?? 0.5;
    this.maxZoom = options.maxZoom ?? 3;
    if (this.minZoom <= 0 || this.maxZoom < this.minZoom) throw new RangeError('Invalid camera zoom range');
    this.zoom = clamp(options.zoom ?? 1.35, this.minZoom, this.maxZoom);
    this.panX = options.panX ?? 0;
    this.panY = options.panY ?? 0;
    this.targetZoom = this.zoom;
    this.targetX = this.panX;
    this.targetY = this.panY;
  }

  gridToScreen(x: number, y: number, elevation = 0): ScreenPoint { return { x: (x - y) * this.tileWidth * 0.5 * this.zoom + this.panX, y: (x + y) * this.tileHeight * 0.5 * this.zoom - elevation * this.zoom + this.panY }; }
  screenToGrid(x: number, y: number, elevation = 0): GridPoint { const localX = (x - this.panX) / this.zoom; const localY = (y - this.panY + elevation * this.zoom) / this.zoom; return { x: (localX / (this.tileWidth * 0.5) + localY / (this.tileHeight * 0.5)) * 0.5, y: (localY / (this.tileHeight * 0.5) - localX / (this.tileWidth * 0.5)) * 0.5, elevation }; }

  hitTest(x: number, y: number, elevationAt: (x: number, y: number) => number | undefined): GridPoint | null {
    const approximate = this.screenToGrid(x, y);
    const baseX = Math.round(approximate.x);
    const baseY = Math.round(approximate.y);
    let best: GridPoint | null = null;
    let depth = -Infinity;
    for (let gridY = baseY - 2; gridY <= baseY + 2; gridY += 1) for (let gridX = baseX - 2; gridX <= baseX + 2; gridX += 1) {
      const elevation = elevationAt(gridX, gridY);
      if (elevation === undefined) continue;
      const point = this.screenToGrid(x, y, elevation);
      if (Math.abs(point.x - gridX) <= 0.5 && Math.abs(point.y - gridY) <= 0.5 && gridX + gridY >= depth) { best = { x: gridX, y: gridY, elevation }; depth = gridX + gridY; }
    }
    return best;
  }

  setZoomAt(zoom: number, screenX: number, screenY: number, immediate = false): void { const next = clamp(zoom, this.minZoom, this.maxZoom); const ratio = next / this.zoom; this.targetZoom = next; this.targetX = screenX - (screenX - this.panX) * ratio; this.targetY = screenY - (screenY - this.panY) * ratio; if (immediate) { this.zoom = next; this.panX = this.targetX; this.panY = this.targetY; } }
  panBy(dx: number, dy: number): void { this.panX += dx; this.panY += dy; this.targetX = this.panX; this.targetY = this.panY; }

  focusGrid(x: number, y: number, viewportWidth: number, viewportHeight: number, zoom = this.zoom, duration = 350, elevation = 0, now = performance.now()): void {
    const point = this.gridToScreen(x, y, elevation);
    this.focus = { startX: this.panX, startY: this.panY, startZoom: this.zoom, endX: this.panX + viewportWidth * 0.5 - point.x, endY: this.panY + viewportHeight * 0.52 - point.y, endZoom: clamp(zoom, this.minZoom, this.maxZoom), start: now, duration: Math.max(1, duration) };
    this.velocityX = 0;
    this.velocityY = 0;
  }

  update(deltaSeconds: number, now = performance.now()): void {
    if (this.focus) {
      const t = clamp((now - this.focus.start) / this.focus.duration, 0, 1);
      const eased = ease(t);
      this.panX = this.focus.startX + (this.focus.endX - this.focus.startX) * eased;
      this.panY = this.focus.startY + (this.focus.endY - this.focus.startY) * eased;
      this.zoom = this.focus.startZoom + (this.focus.endZoom - this.focus.startZoom) * eased;
      if (t === 1) { this.targetX = this.panX; this.targetY = this.panY; this.targetZoom = this.zoom; this.focus = undefined; }
      return;
    }
    const smoothing = 1 - Math.exp(-Math.max(0, deltaSeconds) * 14);
    this.zoom += (this.targetZoom - this.zoom) * smoothing;
    this.panX += (this.targetX - this.panX) * smoothing;
    this.panY += (this.targetY - this.panY) * smoothing;
    if (!this.dragging) { this.velocityX *= Math.pow(0.004, deltaSeconds); this.velocityY *= Math.pow(0.004, deltaSeconds); this.panX += this.velocityX * deltaSeconds; this.panY += this.velocityY * deltaSeconds; this.targetX = this.panX; this.targetY = this.panY; }
  }

  onWheel(event: WheelEvent): void { event.preventDefault(); this.setZoomAt(this.targetZoom * Math.exp(-event.deltaY * 0.0015), event.offsetX, event.offsetY); }
  onPointerDown(event: PointerEvent): void { this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY }); this.dragging = true; this.lastX = event.clientX; this.lastY = event.clientY; this.lastTime = performance.now(); if (this.pointers.size === 2) this.pinchDistance = this.pointerDistance(); }
  onPointerMove(event: PointerEvent, rect: DOMRect): void { if (!this.pointers.has(event.pointerId)) return; this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY }); if (this.pointers.size === 2) { const distance = this.pointerDistance(); const center = this.pointerCenter(); if (this.pinchDistance > 0) this.setZoomAt(this.targetZoom * distance / this.pinchDistance, center.x - rect.left, center.y - rect.top, true); this.pinchDistance = distance; return; } const now = performance.now(); const dx = event.clientX - this.lastX; const dy = event.clientY - this.lastY; const delta = Math.max(0.001, (now - this.lastTime) / 1000); this.panBy(dx, dy); this.velocityX = dx / delta; this.velocityY = dy / delta; this.lastX = event.clientX; this.lastY = event.clientY; this.lastTime = now; }
  onPointerUp(event: PointerEvent): void { this.pointers.delete(event.pointerId); this.dragging = this.pointers.size > 0; this.pinchDistance = this.pointers.size === 2 ? this.pointerDistance() : 0; }

  attach(canvas: HTMLCanvasElement): () => void {
    const wheel = (event: WheelEvent) => this.onWheel(event);
    const down = (event: PointerEvent) => { canvas.setPointerCapture(event.pointerId); this.onPointerDown(event); };
    const move = (event: PointerEvent) => this.onPointerMove(event, canvas.getBoundingClientRect());
    const up = (event: PointerEvent) => this.onPointerUp(event);
    canvas.addEventListener('wheel', wheel, { passive: false }); canvas.addEventListener('pointerdown', down); canvas.addEventListener('pointermove', move); canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
    return () => { canvas.removeEventListener('wheel', wheel); canvas.removeEventListener('pointerdown', down); canvas.removeEventListener('pointermove', move); canvas.removeEventListener('pointerup', up); canvas.removeEventListener('pointercancel', up); };
  }

  private pointerDistance(): number { const [a, b] = [...this.pointers.values()]; return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0; }
  private pointerCenter(): ScreenPoint { const [a, b] = [...this.pointers.values()]; return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : { x: 0, y: 0 }; }
}
